// Customer documents management section with PII scanning
import { useState } from 'react'
import { Upload, FileText, Trash2, X, Shield } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCustomerDocuments } from '@/hooks/useCustomerDocuments'
import { PIIApprovalModal } from '@/components/pii/PIIApprovalModal'
import { cn } from '@/lib/utils'

interface CustomerDocumentsSectionProps {
  customerId: string
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function CustomerDocumentsSection({ customerId }: CustomerDocumentsSectionProps) {
  const {
    documents, isLoading, upload, uploadAfterApproval, deleteDocument,
    isUploading, isDeleting, isScanning, piiResult, clearPiiResult,
  } = useCustomerDocuments(customerId)
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [pendingUpload, setPendingUpload] = useState<{ file: File; title: string; description: string } | null>(null)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) {
      setFile(dropped)
      if (!title) setTitle(dropped.name.replace(/\.[^.]+$/, ''))
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) {
      setFile(selected)
      if (!title) setTitle(selected.name.replace(/\.[^.]+$/, ''))
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return

    // Save pending upload data for PII approval flow
    setPendingUpload({ file, title: title.trim() || file.name, description: description.trim() })

    const result = await upload({
      file,
      title: title.trim() || file.name,
      description: description.trim() || undefined,
    })

    // If upload returned null, PII was found — modal will show
    if (result !== null) {
      resetForm()
    }
  }

  const handlePIIApproval = async () => {
    if (!pendingUpload) return
    try {
      await uploadAfterApproval({
        file: pendingUpload.file,
        title: pendingUpload.title,
        description: pendingUpload.description || undefined,
      })
      resetForm()
    } catch (err) {
      alert('Failed to upload: ' + (err as Error).message)
    }
  }

  const handlePIICancel = () => {
    clearPiiResult()
    setPendingUpload(null)
  }

  const resetForm = () => {
    setFile(null)
    setTitle('')
    setDescription('')
    setPendingUpload(null)
  }

  const handleDelete = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return
    try {
      await deleteDocument(docId)
    } catch (err) {
      alert('Failed to delete: ' + (err as Error).message)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" />
          Customer Documents
        </CardTitle>
        <CardDescription className="text-xs">
          Upload documents that AI agents can reference. Files are scanned for PII before storage.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Form */}
        <form onSubmit={handleUpload} className="space-y-3 rounded-lg border p-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={cn(
              'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors',
              dragOver ? 'border-kaycoo-teal-dark bg-kaycoo-teal-pale/20' : 'border-gray-300',
              file ? 'border-green-400 bg-green-50' : ''
            )}
          >
            {file ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{file.name}</span>
                <span className="text-gray-500">({formatBytes(file.size)})</span>
                <button type="button" onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="mb-2 h-6 w-6 text-gray-400" />
                <p className="text-xs text-gray-600">Drag & drop or click to browse</p>
                <p className="text-xs text-gray-400">PDF, DOCX, TXT, MD (max 10 MB)</p>
                <input type="file" accept=".pdf,.docx,.txt,.md"
                  onChange={handleFileSelect} className="absolute inset-0 cursor-pointer opacity-0" />
              </>
            )}
          </div>

          {file && (
            <>
              <div>
                <Label htmlFor="doc-title" className="text-xs">Title</Label>
                <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="Document title..." className="text-sm" />
              </div>
              <div>
                <Label htmlFor="doc-desc" className="text-xs">Description (optional)</Label>
                <Textarea id="doc-desc" value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description..." rows={2} className="text-sm" />
              </div>
              <Button type="submit" disabled={isUploading || isScanning}
                className="w-full gap-1.5 bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2" size="sm">
                {isScanning ? (
                  <><Shield className="h-3.5 w-3.5 animate-pulse" /> Scanning for PII...</>
                ) : isUploading ? 'Uploading...' : (
                  <><Shield className="h-3.5 w-3.5" /> Upload & Scan</>
                )}
              </Button>
            </>
          )}
        </form>

        {/* Documents List */}
        <div className="space-y-2">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-kaycoo-teal-dark border-t-transparent" />
            </div>
          ) : documents.length === 0 ? (
            <p className="py-4 text-center text-xs text-gray-500">No documents uploaded yet</p>
          ) : (
            documents.map((doc) => (
              <div key={doc.id} className="flex items-start justify-between gap-2 rounded-lg border p-3 text-xs">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-kaycoo-teal-dark" />
                    <span className="font-medium">{doc.title || doc.file_name}</span>
                  </div>
                  {doc.description && <p className="text-gray-600">{doc.description}</p>}
                  <div className="flex items-center gap-2 text-gray-400">
                    <span>{doc.file_name}</span>
                    <span>·</span>
                    <span>{formatBytes(doc.file_size_bytes)}</span>
                    <span>·</span>
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)}
                  disabled={isDeleting} className="h-7 px-2 text-gray-400 hover:text-red-500">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* PII Approval Modal */}
      {piiResult !== null && (
        <PIIApprovalModal
          items={piiResult.pii_found}
          redactedText={piiResult.redacted_text}
          onApprove={handlePIIApproval}
          onCancel={handlePIICancel}
        />
      )}
    </Card>
  )
}
